/* Amplitude — product analytics + session replay.
 *
 * ENVIRONMENT GATE. `VITE_AMPLITUDE_ENABLED` is a BUILD-time value, supplied by
 * the matching GitHub Environment variable in _deploy-contractor-web.yml, so dev
 * and prod are switched independently from the GitHub UI with no code change.
 * Anything other than the string "true" — including the variable being unset,
 * which is the case for `npm run dev` and for the whole Playwright suite — means
 * the SDK never initialises and nothing leaves the browser.
 *
 * This is the ONE build-time value in the bundle, and it is a deliberate
 * exception to the rule config.ts states: everything else resolves at runtime by
 * hostname so a single artefact is provably identical across environments. That
 * argument does not hold here, because the entire point of this flag is for the
 * artefact to behave DIFFERENTLY per environment. Keeping the exception to one
 * boolean is what stops it spreading.
 *
 * A failed analytics install must never be able to take the app down with it, so
 * nothing here throws and nothing here is awaited by a caller.
 */
import * as amplitude from "@amplitude/unified";

/* Amplitude ingestion key — public by design, exactly like the Firebase web key
 * and the Places browser key in config.ts. It identifies the project for writes
 * and grants no read access. Kept here rather than in a build variable so that a
 * missing CI variable can only ever turn analytics OFF (a loud, obvious state)
 * rather than half-configure it. */
const AMPLITUDE_API_KEY = "4fa37eccabd093e003a41eba35f9d652";

const ENABLED = import.meta.env.VITE_AMPLITUDE_ENABLED === "true";

let started = false;

/**
 * Boot Amplitude. Safe to call more than once; only the first call does work.
 *
 * Called from main.tsx at module scope rather than from a component, because
 * the app root is wrapped in StrictMode — a mount effect would run twice in
 * development and register the SDK twice.
 */
export function initAnalytics(): void {
  if (started) return;
  started = true;
  if (!ENABLED) return;

  if (!AMPLITUDE_API_KEY) {
    console.warn("Amplitude API key missing — analytics disabled");
    return;
  }

  void amplitude.initAll(AMPLITUDE_API_KEY, {
    serverZone: "EU",
    analytics: { autocapture: true },
    sessionReplay: {
      sampleRate: 1,
      /* POPIA. This app takes bank account numbers and ID document numbers
       * (BankDetailsScreen) and renders entire homeowner conversations
       * (MessageThread). rrweb is configured with maskAllInputs unconditionally,
       * which covers what a contractor TYPES, but not what is rendered back to
       * them — a saved account number, a homeowner's address in a message.
       * 'conservative' masks text nodes as well, which is the only level that
       * covers both.
       *
       * ponytail: one global mask level, because a selector allowlist rots the
       * first time someone renames a class and fails OPEN — silently shipping
       * the field it was meant to hide. If replays turn out to be too redacted
       * to be useful, relax per-route with `urlMaskLevels` (never globally), or
       * name specific safe elements with `unmaskSelector`. */
      privacyConfig: { defaultMaskLevel: "conservative" },
    },
    /* initAll adds the Engagement (Guides & Surveys) SDK whether or not you
     * configure it, and that plugin injects <script src="cdn.eu.amplitude.com">
     * into <head>. We want neither the feature nor the third-party origin:
     * allowing it would mean widening script-src, which headers.spec.ts asserts
     * is exactly "script-src 'self'". `skip` stops the bundle ever loading. */
    engagement: { skip: true },
  });
}

/**
 * Record a product event.
 *
 * Gated on the same flag as init: with analytics off the SDK was never
 * initialised, and an ungated `amplitude.track` would queue events into an
 * unconfigured client that can never flush them.
 */
export function track(event: string, properties?: Record<string, unknown>): void {
  if (!ENABLED) return;
  amplitude.track(event, properties);
}
